import { makeRequest, apiClient } from '../../../../services/apiClient';

const isNotFound = (err) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message || err || '');
  return /Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg);
};

const getBackendOrigin = () => {
  try {
    const base = apiClient?.defaults?.baseURL || '';
    if (!base) return null;
    const u = new URL(base, window?.location?.origin);
    return u.origin;
  } catch (e) {
    return null;
  }
};

/**
 * Fetch KPI for objects never referenced by any event (orphaned objects).
 * Tries primary, alias, and simple endpoints; computes ratio with total-objects if needed.
 */
export const fetchOrphanObjectsKPI = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};

  // Primary endpoints first - KPI router is mounted at /api/kpi
  try {
    return await makeRequest('/kpi/orphan-objects', { params }, 'orphan objects KPI');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }
  try {
    return await makeRequest('/orphan-objects', { params }, 'orphan objects KPI (legacy)');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }

  // Simple endpoints
  let simple;
  try {
    simple = await makeRequest('/orphan-objects-simple', { params }, 'orphan objects KPI (simple)');
  } catch (e3) {
    if (!isNotFound(e3)) throw e3;
    try {
      simple = await makeRequest('/analytics/orphan-objects-simple', { params }, 'orphan objects KPI (simple alias)');
    } catch (e4) {
      if (!isNotFound(e4)) throw e4;

      // Try broader candidates for various deployments
      if (typeof window !== 'undefined' && window.location) {
        const origin = getBackendOrigin() || window.location.origin;
        const candidates = [
          // Primary
          `${origin}/orphan-objects`,
          `${origin}/api/orphan-objects`,
          `${origin}/api/v1/orphan-objects`,
          `${origin}/v1/orphan-objects`,
          `${origin}/analytics/orphan-objects`,
          `${origin}/api/analytics/orphan-objects`,
          `${origin}/api/v1/analytics/orphan-objects`,
          `${origin}/v1/analytics/orphan-objects`,
          // Simple
          `${origin}/orphan-objects-simple`,
          `${origin}/api/orphan-objects-simple`,
          `${origin}/api/v1/orphan-objects-simple`,
          `${origin}/v1/orphan-objects-simple`,
          `${origin}/analytics/orphan-objects-simple`,
          `${origin}/api/analytics/orphan-objects-simple`,
          `${origin}/api/v1/analytics/orphan-objects-simple`,
          `${origin}/v1/analytics/orphan-objects-simple`,
          // Gateway variants
          `${origin}/gateway/api/orphan-objects`,
          `${origin}/gateway/api/analytics/orphan-objects`,
          `${origin}/gateway/api/orphan-objects-simple`,
          `${origin}/gateway/api/analytics/orphan-objects-simple`,
        ];
        for (const url of candidates) {
          try {
            if (/orphan-objects(?!-simple)/.test(url)) {
              const res = await makeRequest(url, { params }, `orphan objects KPI (${url})`);
              if (res && typeof res === 'object' && 'total_objects' in res) return res;
            }
          } catch (e5) {
            if (!isNotFound(e5)) throw e5;
          }
        }
        for (const url of candidates) {
          if (/orphan-objects-simple/.test(url)) {
            try {
              simple = await makeRequest(url, { params }, `orphan objects KPI (simple ${url})`);
              if (simple) break;
            } catch (e6) {
              if (!isNotFound(e6)) throw e6;
            }
          }
        }
      }
    }
  }

  const orphanCount = typeof simple?.orphan_objects === 'number' ? simple.orphan_objects : 0;

  // Get total objects to compute ratio
  let total = 0;
  try {
    const totalResp = await makeRequest('/total-objects', { params }, 'total objects for orphan objects');
    total = typeof totalResp?.totalObjects === 'number' ? totalResp.totalObjects : 0;
  } catch (te) {
    if (!isNotFound(te)) throw te;
    if (typeof window !== 'undefined' && window.location) {
      const origin = getBackendOrigin() || window.location.origin;
      const totalCandidates = [
        `${origin}/total-objects`,
        `${origin}/api/total-objects`,
        `${origin}/api/v1/total-objects`,
        `${origin}/v1/total-objects`,
        `${origin}/ocel/total-objects`,
        `${origin}/api/ocel/total-objects`,
        `${origin}/gateway/api/total-objects`,
        `${origin}/gateway/api/ocel/total-objects`,
      ];
      for (const url of totalCandidates) {
        try {
          const totalRespX = await makeRequest(url, { params }, `total objects for orphan objects (${url})`);
          total = typeof totalRespX?.totalObjects === 'number' ? totalRespX.totalObjects : 0;
          if (total > 0) break;
        } catch (e7) {
          if (!isNotFound(e7)) throw e7;
        }
      }
    }
  }

  const ratio = total > 0 ? orphanCount / total : 0;
  return {
    file_type: fileType ?? 'main',
    total_objects: total,
    orphan_objects: orphanCount,
    orphan_ratio: ratio,
    considered_keys: ['ocel:objects', 'objects', 'ocel:omap', 'omap']
  };
};
